/*
 *  -----------------------------------------------------------------------
 *    <copyright file="file-uploader/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'clsx';
import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FileUploaderProps } from '@components/core/file-uploader/file-uploader.interface';
import { useTranslation } from 'hooks';
import { isJson } from 'utils/validation';

import './styles.scss';

const FileUploader = ({ acceptedFileTypes, onFileUpload, error, onError, showFileName }: FileUploaderProps) => {
    const { t } = useTranslation();
    const [fileError, setFileError] = useState('');
    const [fileName, setFileName] = useState('');

    const verifyFile = (files: File[]) =>
        new Promise((res) => {
            if (files?.length) {
                const currentFile = files[0];
                const reader = new FileReader();
                setFileName(currentFile.name);
                reader.addEventListener(
                    'load',
                    () => {
                        const isValid = isJson(reader.result);
                        const isError = isValid ? null : t('admin:invalidJsonFile');
                        setFileError(isError);
                        if (onError) {
                            onError(isError);
                        }
                        res(isValid ? reader.result : null);
                    },
                    false,
                );
                reader.readAsText(currentFile);
            }
        });

    const onDrop = async (acceptedFiles: File[]) => {
        if (acceptedFiles?.length > 0) {
            const jsonData: any = await verifyFile(acceptedFiles);
            if (jsonData) onFileUpload(JSON.parse(jsonData));
        } else {
            // They dropped an invalid file type
            setFileError(t('admin:fileNotUploadedTryAgain'));
            if (onError) {
                onError(t('admin:fileNotUploadedTryAgain'));
            }
        }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: acceptedFileTypes,
        multiple: false,
    });

    return (
        <div className="file-error-wrapper">
            <div className={cn('file-wrapper', { error: fileError || error })}>
                <div className="drag-and-drop-area" {...getRootProps()} data-testid="drag-and-drop-area">
                    <input {...getInputProps()} />
                    <FontAwesomeIcon icon={['fal', 'cloud-upload']} className="upload-icon icon" />
                    <p>{t(isDragActive ? 'admin:dropTheFilesHere' : 'admin:dragOrClickToUpload')}</p>
                </div>
                {showFileName && fileName && <p className="file-name">{fileName}</p>}
            </div>
            {!!(error || fileError) && (
                <span id="imgSrc-help-text-error" className="remaining-text red">
                    {error || fileError}
                </span>
            )}
        </div>
    );
};

export default FileUploader;
